import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { markMaterialPurchased, createConsumable, getConsumables, restockConsumable, getShopConfig, splitMaterial, updateProject } from '../utils/api';
import { useToast } from '../context/ToastContext';

const CATEGORIES = ['filter', 'bulb', 'battery', 'cleaning', 'plumbing', 'hvac', 'other'];

const parseQty = (str) => {
  if (!str) return 1;
  const n = parseFloat(str);
  return isNaN(n) ? 1 : n;
};

// See buildShopUrls note in ProjectDetail.js — same URL templates apply here.
const buildShopUrls = (name, config) => {
  if (!config) return {};
  const q = encodeURIComponent(name);
  const urls = {};
  if (config.amazon) urls.amazon = `https://www.amazon.com/s?k=${q}&tag=${config.amazon}`;
  if (config.homeDepot) urls.homeDepot = `https://www.homedepot.com/s/${q}`;
  if (config.lowes) urls.lowes = `https://www.lowes.com/search?searchTerm=${q}`;
  return urls;
};

const ShoppingList = () => {
  const { projects, fetchProjects, patchProjectMaterial, patchProjectMaterials } = useApp();
  const { showToast } = useToast();
  const [showPurchased, setShowPurchased] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState({});

  // Low-stock consumables
  const [lowStock, setLowStock] = useState([]);
  const [restockForms, setRestockForms] = useState({});
  const [restockSaving, setRestockSaving] = useState({});
  const [restocked, setRestocked] = useState({});

  // Affiliate shop config
  const [shopConfig, setShopConfig] = useState(null);

  // Split state
  const [splittingKey, setSplittingKey] = useState(null);
  const [splitPreview, setSplitPreview] = useState([]);
  const [splitSaving, setSplitSaving] = useState(false);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    getConsumables({ lowStock: 'true' }).then(({ data }) => setLowStock(data));
    getShopConfig().then(({ data }) => setShopConfig(data)).catch(() => {});
  }, []);

  const active = projects.filter(
    (p) => p.status !== 'completed' && p.materials?.length > 0
  );

  const allUnpurchased = active.flatMap((p) =>
    p.materials.map((m, i) => ({ ...m, _projectId: p._id, _index: i }))
  ).filter((m) => !m.purchased);

  const totalEstimate = allUnpurchased.reduce((s, m) => s + (m.estimatedCost || 0), 0);
  const visibleLowStock = lowStock.filter((c) => !restocked[c._id]);

  const key = (projectId, index) => `${projectId}-${index}`;

  const handleGotIt = (project, index, material) => {
    const k = key(project._id, index);
    setExpanded((prev) => ({ ...prev, [k]: true }));
    setForms((prev) => ({
      ...prev,
      [k]: {
        name: material.name,
        qty: parseQty(material.quantity),
        unitCost: material.estimatedCost ?? '',
        category: 'other',
      },
    }));
  };

  const handleFormChange = (k, field, value) => {
    setForms((prev) => ({ ...prev, [k]: { ...prev[k], [field]: value } }));
  };

  const closeExpanded = (k) => {
    setExpanded((prev) => { const n = { ...prev }; delete n[k]; return n; });
    setForms((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };

  const handleAddToInventory = async (project, index) => {
    const k = key(project._id, index);
    const form = forms[k];
    setSaving((prev) => ({ ...prev, [k]: true }));
    try {
      await createConsumable({
        name: form.name,
        category: form.category,
        quantityOnHand: parseFloat(form.qty) || 0,
        unitCost: form.unitCost !== '' ? Number(form.unitCost) : undefined,
        lastRestockedAt: new Date(),
      });
      await markMaterialPurchased(project._id, index);
      patchProjectMaterial(project._id, index, { purchased: true });
      closeExpanded(k);
    } finally {
      setSaving((prev) => { const n = { ...prev }; delete n[k]; return n; });
    }
  };

  const handleSkip = async (project, index) => {
    const k = key(project._id, index);
    setSaving((prev) => ({ ...prev, [k]: true }));
    try {
      await markMaterialPurchased(project._id, index);
      patchProjectMaterial(project._id, index, { purchased: true });
      closeExpanded(k);
    } finally {
      setSaving((prev) => { const n = { ...prev }; delete n[k]; return n; });
    }
  };

  // Restock handlers
  const handleRestockOpen = (c) => {
    setRestockForms((prev) => ({ ...prev, [c._id]: { qty: c.reorderQty || 1 } }));
  };

  const handleRestockConfirm = async (c) => {
    const bought = parseFloat(restockForms[c._id]?.qty);
    const qty = isNaN(bought) ? 0 : bought;
    setRestockSaving((prev) => ({ ...prev, [c._id]: true }));
    try {
      // Restock sets the absolute quantity, so add what was bought to what's on hand
      await restockConsumable(c._id, c.quantityOnHand + qty);
      setRestocked((prev) => ({ ...prev, [c._id]: true }));
      setRestockForms((prev) => { const n = { ...prev }; delete n[c._id]; return n; });
    } finally {
      setRestockSaving((prev) => { const n = { ...prev }; delete n[c._id]; return n; });
    }
  };

  const splitKey = (projectId, index) => `split-${projectId}-${index}`;

  const handleSplit = async (project, index, material) => {
    const k = splitKey(project._id, index);
    setSplittingKey(k);
    setSplitPreview([]);
    try {
      const { data } = await splitMaterial(material.name, project.title);
      if (data.items.length <= 1) {
        showToast('This looks like a single item — no split needed', 'info');
        setSplittingKey(null);
        return;
      }
      setSplitPreview(data.items);
    } catch (err) {
      showToast(err.response?.data?.message || 'Split failed', 'error');
      setSplittingKey(null);
    }
  };

  const handleSplitConfirm = async (project, index) => {
    const newMaterials = [
      ...project.materials.slice(0, index),
      ...splitPreview,
      ...project.materials.slice(index + 1),
    ];
    setSplitSaving(true);
    try {
      await updateProject(project._id, { materials: newMaterials });
      patchProjectMaterials(project._id, newMaterials);
      setSplittingKey(null);
      setSplitPreview([]);
      showToast(`Split into ${splitPreview.length} items`, 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save split', 'error');
    } finally {
      setSplitSaving(false);
    }
  };

  const cancelSplit = () => { setSplittingKey(null); setSplitPreview([]); };

  const hasShopLinks = shopConfig && (shopConfig.amazon || shopConfig.homeDepot || shopConfig.lowes);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Shopping List</h1>
          {(allUnpurchased.length > 0 || visibleLowStock.length > 0) && (
            <p className="page-subtitle">
              {[
                allUnpurchased.length > 0 && `${allUnpurchased.length} project item${allUnpurchased.length !== 1 ? 's' : ''}`,
                visibleLowStock.length > 0 && `${visibleLowStock.length} supply restock${visibleLowStock.length !== 1 ? 's' : ''}`,
              ].filter(Boolean).join(' · ')}
              {totalEstimate > 0 && ` · est. $${totalEstimate.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            </p>
          )}
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setShowPurchased((v) => !v)}
        >
          {showPurchased ? 'Hide purchased' : 'Show purchased'}
        </button>
      </div>

      {active.length === 0 && visibleLowStock.length === 0 && (
        <div className="empty-state">
          <p>Nothing to buy right now.</p>
          <p style={{ marginTop: '0.5rem' }}>
            <Link to="/projects/intake">Start a project with AI →</Link>
          </p>
        </div>
      )}

      {/* Low-stock supplies */}
      {visibleLowStock.length > 0 && (
        <section>
          <div className="section-header">
            <h2>Supplies to Restock</h2>
            <span className="badge" style={{ background: 'var(--warning)', color: '#fff' }}>
              {visibleLowStock.length} low
            </span>
          </div>
          <div className="shop-list">
            {visibleLowStock.map((c) => {
              const isOpen = !!restockForms[c._id];
              const isSaving = restockSaving[c._id];
              return (
                <div key={c._id} className="shop-item">
                  <div className="shop-item-main">
                    <div className="shop-item-info">
                      <span className="shop-item-name">{c.name}</span>
                      <div className="shop-item-meta">
                        <span className="badge">{c.category}</span>
                        <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                          {c.quantityOnHand} on hand · need {c.reorderAt + 1}+
                        </span>
                        {c.storeName && <span>{c.storeName}</span>}
                        {c.unitCost != null && (
                          <span className="shop-item-cost">~${c.unitCost} ea</span>
                        )}
                      </div>
                    </div>
                    {!isOpen && (
                      <button className="btn btn-success btn-sm" onClick={() => handleRestockOpen(c)}>
                        Restock
                      </button>
                    )}
                  </div>
                  {isOpen && (
                    <div className="shop-item-form">
                      <p className="shop-form-label">How many did you buy?</p>
                      <div className="shop-form-actions" style={{ alignItems: 'center' }}>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={restockForms[c._id]?.qty}
                          onChange={(e) => setRestockForms((prev) => ({ ...prev, [c._id]: { qty: e.target.value } }))}
                          style={{ width: '80px', padding: '0.4rem 0.6rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '0.88rem' }}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleRestockConfirm(c)}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving…' : 'Confirm'}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setRestockForms((prev) => { const n = { ...prev }; delete n[c._id]; return n; })}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Project materials */}
      {active.map((project) => {
        const visible = project.materials.filter((m) => showPurchased || !m.purchased);
        if (visible.length === 0) return null;

        return (
          <section key={project._id}>
            <div className="section-header">
              <h2>
                <Link to={`/projects/${project._id}`} className="shop-project-link">
                  {project.title}
                </Link>
              </h2>
              <span className={`badge badge-status-${project.status}`}>
                {project.status.replace('-', ' ')}
              </span>
            </div>
            <div className="shop-list">
              {project.materials.map((material, index) => {
                if (!showPurchased && material.purchased) return null;
                const k = key(project._id, index);
                const sk = splitKey(project._id, index);
                const isExpanded = expanded[k];
                const isSaving = saving[k];
                const form = forms[k] || {};
                const isSplittingThis = splittingKey === sk;
                const isWaitingSplit = isSplittingThis && splitPreview.length === 0;
                const shopUrls = material.purchased ? {} : buildShopUrls(material.name, shopConfig);

                return (
                  <div key={index} className={`shop-item${material.purchased ? ' shop-item-purchased' : ''}`}>
                    <div className="shop-item-main">
                      <div className="shop-item-info">
                        <span className="shop-item-name">{material.name}</span>
                        <div className="shop-item-meta">
                          {material.quantity && <span>{material.quantity}</span>}
                          {material.estimatedCost != null && (
                            <span className="shop-item-cost">
                              ~${material.estimatedCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          )}
                          {material.notes && <span className="shop-item-note">{material.notes}</span>}
                        </div>
                        {Object.keys(shopUrls).length > 0 && (
                          <div className="material-shop-links">
                            {shopUrls.amazon && <a href={shopUrls.amazon} target="_blank" rel="noopener noreferrer sponsored" className="shop-link shop-link-amazon">Amazon</a>}
                            {shopUrls.homeDepot && <a href={shopUrls.homeDepot} target="_blank" rel="noopener noreferrer sponsored" className="shop-link shop-link-hd">HD</a>}
                            {shopUrls.lowes && <a href={shopUrls.lowes} target="_blank" rel="noopener noreferrer sponsored" className="shop-link shop-link-lowes">Lowe's</a>}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                        {!material.purchased && (
                          <button
                            className="btn btn-secondary btn-sm split-btn"
                            onClick={() => handleSplit(project, index, material)}
                            disabled={splittingKey !== null || isExpanded}
                            title="Split into individual items"
                          >
                            {isWaitingSplit ? '…' : '✂'}
                          </button>
                        )}
                        {material.purchased ? (
                          <span className="shop-purchased-badge">✓ Purchased</span>
                        ) : (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleGotIt(project, index, material)}
                            disabled={isExpanded}
                          >
                            Got it
                          </button>
                        )}
                      </div>
                    </div>

                    {isSplittingThis && splitPreview.length > 0 && (
                      <div className="split-preview">
                        <p className="split-preview-label">Split into {splitPreview.length} items:</p>
                        <ul className="split-preview-list">
                          {splitPreview.map((item, j) => (
                            <li key={j}>
                              <span className="split-preview-name">{item.name}</span>
                              {item.quantity && <span className="split-preview-meta">{item.quantity}</span>}
                              {item.estimatedCost != null && <span className="split-preview-meta">~${item.estimatedCost}</span>}
                            </li>
                          ))}
                        </ul>
                        <div className="split-preview-actions">
                          <button className="btn btn-primary btn-sm" onClick={() => handleSplitConfirm(project, index)} disabled={splitSaving}>
                            {splitSaving ? 'Saving…' : 'Confirm Split'}
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={cancelSplit} disabled={splitSaving}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {isExpanded && (
                      <div className="shop-item-form">
                        <p className="shop-form-label">Add to Supplies inventory?</p>
                        <div className="shop-form-row">
                          <label>
                            Name
                            <input
                              value={form.name}
                              onChange={(e) => handleFormChange(k, 'name', e.target.value)}
                            />
                          </label>
                          <label>
                            Qty
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={form.qty}
                              onChange={(e) => handleFormChange(k, 'qty', e.target.value)}
                            />
                          </label>
                          <label>
                            Unit cost ($)
                            <input
                              type="number"
                              min="0"
                              value={form.unitCost}
                              onChange={(e) => handleFormChange(k, 'unitCost', e.target.value)}
                            />
                          </label>
                          <label>
                            Category
                            <select
                              value={form.category}
                              onChange={(e) => handleFormChange(k, 'category', e.target.value)}
                            >
                              {CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="shop-form-actions">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleAddToInventory(project, index)}
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving…' : 'Add to Supplies'}
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleSkip(project, index)}
                            disabled={isSaving}
                          >
                            Skip
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
      {hasShopLinks && <p className="shop-disclosure" style={{ marginTop: '0.75rem' }}>Links may earn a commission.</p>}
    </div>
  );
};

export default ShoppingList;
